import React from 'react'
import styled from 'styled-components'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LinkOffIcon from '@mui/icons-material/LinkOff'

import Card from 'pages/HehmDevice/HehmDashboard/Card'
import LastUpdatedText from 'components/LastUpdatedText'
import PressureValue from 'pages/HehmDevice/HehmDashboard/SystemConditionTabContent/PressureValue'
import SysStatusIcon from 'pages/HehmDevice/HehmDashboard/SysStatusIcon'
import TabContentTitle from 'pages/HehmDevice/TabContentTitle'
import TemperatureValue from 'pages/HehmDevice/HehmDashboard/SystemConditionTabContent/TemperatureValue'
import NoDataValue from 'pages/HehmDevice/HehmDashboard/NoDataValue'

import { amps, capitalize } from 'helpers/formatters'

import { color } from 'styles/colors'

import HehmDevice from 'types/HehmDevice'

const HEALTH_TEXT: Record<string, string> = {
  GREEN: 'Overall system health is good.',
  YELLOW: 'System needs maintenance.',
  RED: 'Overall system needs attention.',
  UNKNOWN: ''
}

const SystemConditionTabContent = ({ hehmDevice }: { hehmDevice: HehmDevice | null }) => {
  const healthData = hehmDevice?.health

  const indoorUnit = healthData?.current.sensors.indoor
  const outdoorUnit = healthData?.current.sensors.outdoor

  const filterHealthPercentage = healthData?.filter?.lifeRemaining ? healthData?.filter?.lifeRemaining + '%' : null
  const filterHealthStatus = capitalize(healthData?.filter.category?.replace(/_/g, ' '))

  const isConnected = hehmDevice?.connectionStatus?.connected
  const disconnectedDuration = hehmDevice ? Date.now() - new Date(hehmDevice?.connectionStatus?.lastConnectedAt).getTime() : 0
  const isRecentlyDisconnected = !isConnected && disconnectedDuration < 24 * 60 * 60 * 1000
  const isDisconnectedForLessThanOneWeek = !isConnected && disconnectedDuration <= 7 * 24 * 60 * 60 * 1000

  const getConnectionStatusText = () => {
    if (isConnected) return 'Connected'
    if (isRecentlyDisconnected) return 'Disconnected < 24 hours'
    if (isDisconnectedForLessThanOneWeek) return 'Disconnected for at least 24 hours'
    return 'Disconnected for at least 1 week'
  }
  const connectionStatusText = getConnectionStatusText()

  const healthIconUrl = `/img/health_icon_${healthData?.category.toLowerCase()}.svg`

  const getSystemStatus = () => {
    const mode = healthData?.current.hvacMode
    if (mode?.match(/heating/gi)) return 'Heating'
    if (mode?.match(/cooling/gi)) return 'Cooling'
    return capitalize(mode)
  }

  const isMissingSensor = (sensorName: string) => {
    return healthData?.current.missingSensors?.includes(sensorName)
  }

  return (
    <>
      <TabContentTitle>System Condition</TabContentTitle>
      <LastUpdatedText timestamp={Date.now()} />

      <S.Cards>
        <Card title="Health" icon={healthIconUrl}>
          <S.HealthText>{HEALTH_TEXT[healthData?.category?.toUpperCase() || 'UNKNOWN']}</S.HealthText>

          <Card.ItemLabel>Filter Health</Card.ItemLabel>
          <S.FilterHealthValue>
            <S.FilterIcon />
            {filterHealthPercentage} {filterHealthStatus || <NoDataValue />}
          </S.FilterHealthValue>

          <Card.ItemLabel>Communication Status</Card.ItemLabel>
          <Card.ItemValue>
            {!isConnected && <S.ConnectionStatusIcon {...{ isRecentlyDisconnected, isDisconnectedForLessThanOneWeek }} />}
            {connectionStatusText}
          </Card.ItemValue>

          <Card.ItemLabel>System Status</Card.ItemLabel>
          <Card.ItemValue>
            <SysStatusIcon status={healthData?.current.hvacMode || ''} /> {getSystemStatus()}
          </Card.ItemValue>

          <Card.ItemLabel>Relays</Card.ItemLabel>
          <S.Relays>
            <S.Relay isOn={indoorUnit?.y1Status === 1}>y1</S.Relay>
            <S.Relay isOn={indoorUnit?.oStatus === 1}>o</S.Relay>
            <S.Relay isOn={indoorUnit?.w1Status === 1}>w1</S.Relay>
          </S.Relays>
        </Card>

        <Card title="Indoor Unit" icon="/img/indoor_unit.svg">
          <Card.ItemLabel>Return Static Pressure</Card.ItemLabel>
          {isMissingSensor('returnStaticPressure') ? <S.NoDataValue /> : <PressureValue value={indoorUnit?.returnStaticPressure || ''} unit="inWC" />}

          <Card.ItemLabel>Return Air Temperature</Card.ItemLabel>
          {isMissingSensor('returnTemperature') ? <S.NoDataValue /> : <TemperatureValue value={indoorUnit?.returnTemperature || ''} />}

          <Card.ItemLabel>Supply Air Temperature</Card.ItemLabel>
          {isMissingSensor('supplyTemperature') ? <S.NoDataValue /> : <TemperatureValue value={indoorUnit?.supplyTemperature || ''} />}
        </Card>

        <Card title="Outdoor Unit" icon="/img/outdoor_unit.svg">
          <S.OutdoorUnitCardContent>
            <S.Column>
              <Card.ItemLabel>Liquid Temperature</Card.ItemLabel>
              {isMissingSensor('liquidTemperature') ? <S.NoDataValue /> : <TemperatureValue value={outdoorUnit?.liquidTemperature || ''} />}

              <Card.ItemLabel>Liquid Pressure</Card.ItemLabel>
              {isMissingSensor('liquidPressure') ? <S.NoDataValue /> : <PressureValue value={outdoorUnit?.liquidPressure || ''} unit="PSIG" />}

              <Card.ItemLabel>OD Current</Card.ItemLabel>
              {isMissingSensor('oduCurrent') ? <S.NoDataValue /> : <Card.ItemValue>{amps(outdoorUnit?.oduCurrent ?? '')}</Card.ItemValue>}
            </S.Column>

            {/* <S.Column> // Kept all suction related things for future version of hardware kit.
              <Card.ItemLabel>Suction Temperature</Card.ItemLabel>
              {isMissingSensor('suctionTemperature') ? <S.NoDataValue /> : <TemperatureValue value={outdoorUnit?.suctionTemperature || ''} />}

              <Card.ItemLabel>Suction Pressure</Card.ItemLabel>
              {isMissingSensor('suctionPressure') ? <S.NoDataValue /> : <PressureValue value={outdoorUnit?.suctionPressure || ''} unit="PSIG" />}
            </S.Column>} */}

            <S.Column>
              <Card.ItemLabel>OD Gas Temperature</Card.ItemLabel>
              {isMissingSensor('gasTemperature') ? <S.NoDataValue /> : <TemperatureValue value={outdoorUnit?.gasTemperature || ''} />}

              <Card.ItemLabel>OD Gas Pressure</Card.ItemLabel>
              {isMissingSensor('gasPressure') ? <S.NoDataValue /> : <PressureValue value={outdoorUnit?.gasPressure || ''} unit="PSIG" />}
            </S.Column>
          </S.OutdoorUnitCardContent>
        </Card>
      </S.Cards>
    </>
  )
}

export default SystemConditionTabContent

const S = {
  Cards: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 20px;
  `,
  HealthText: styled.p`
    margin-top: -15px;
    margin-bottom: 20px;
  `,
  ConnectionStatusIcon: styled(LinkOffIcon).withConfig({
    shouldForwardProp: (prop) => !['isRecentlyDisconnected', 'isDisconnectedForLessThanOneWeek'].includes(prop)
  })<{ isRecentlyDisconnected: boolean; isDisconnectedForLessThanOneWeek: boolean }>`
    vertical-align: bottom;
    margin-right: 8px;
    height: 25px;
    width: 25px;
    color: ${({ isRecentlyDisconnected, isDisconnectedForLessThanOneWeek }) => {
    if (isRecentlyDisconnected) return color('recentlyDisconnected')
    if (isDisconnectedForLessThanOneWeek) return color('disconnected24Hours')
    return color('disconnected7Days')
  }};
  `,
  FilterIcon: styled.span`
    display: inline-block;
    vertical-align: bottom;
    margin-right: 5px;
    width: 25px;
    height: 25px;
    background-size: contain;
    background-repeat: no-repeat;
    background-image: url(/img/hehm_dirty_air_filter.svg);
  `,
  FilterHealthValue: styled(Card.ItemValue)`
    display: flex;`,
  FilterHealthIcon: styled(ErrorOutlineIcon).withConfig({
    shouldForwardProp: (prop) => prop !== 'status'
  })<{ status: string }>`
    vertical-align: sub;
    margin-right: 9px;
    color: ${({ status }) => status === 'fair' ? color('fair') : color('critical')};
  `,
  OutdoorUnitCardContent: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  `,
  Column: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Relays: styled.div`
    display: flex;
  `,
  Relay: styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'isOn'
  })<{ isOn: boolean }>`
    text-transform: uppercase;
    font-weight: bold;
    background-color: ${(props) => (props.isOn ? color('background') : 'transparent')};
    color: ${(props) => (props.isOn ? color('primaryText') : color('ancillaryText'))};
    border-radius: 4px;
    padding: 4px 8px;
    margin-right: 8px;
  `,
  NoDataValue: styled(NoDataValue)`
    margin-bottom: 15px;
  `
}
